import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { solutions, type Solution, type SolutionKey } from '../../data/solutions';
import { SectionIntro } from '../layout/SectionIntro';
const accent = {
  orange: {
    text: 'text-forge-orange',
    bg: 'bg-forge-orange',
    soft: 'bg-forge-orange/10',
    border: 'border-forge-orange/35'
  },
  green: {
    text: 'text-forge-green',
    bg: 'bg-forge-green',
    soft: 'bg-forge-green/10',
    border: 'border-forge-green/35'
  },
  cyan: {
    text: 'text-forge-cyan',
    bg: 'bg-forge-cyan',
    soft: 'bg-forge-cyan/10',
    border: 'border-forge-cyan/35'
  }
} as const;
export function Solutions() {
  const [active, setActive] = useState<SolutionKey>(solutions[0].key);
  const current = solutions.find(s => s.key === active) ?? solutions[0];
  const tone = accent[current.accent];
  return <section id="solutions" className="relative overflow-hidden bg-ink py-14 lg:py-20" data-id="element-213" data-mp="3hg0t59y0h0kd">
      <div className="absolute inset-0 bp opacity-70" aria-hidden="true" data-id="element-214" data-mp="3i8eq32wu8wcm" />
      <div className="absolute right-0 top-10 h-[32rem] w-[32rem] rounded-full bg-navy-700/25 blur-[140px]" aria-hidden="true" data-id="element-215" data-mp="3n6g95nu9kvo3" />

      <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-216" data-mp="38gr4himyq9j2">
        <SectionIntro eyebrow="What we help improve" title="The visible website is only part of the job." body="We look at the full path—from how customers find you and decide, to how leads, orders, and information move through the business. Then we scope the design, marketing, commerce, integrations, and support needed to improve it." data-id="element-217" data-mp="3dflq0scbec6v" />

        {/* --- small screens: accordion --- */}
        <div className="mt-8 min-w-0 space-y-2.5 lg:hidden" data-id="element-218" data-mp="2ui15tykhw1sl">
          {solutions.map(s => <AccordionItem key={s.key} solution={s} open={s.key === active} onToggle={() => setActive(s.key)} data-id="element-219" data-mp="nrkylwtxm1s9" />)}
        </div>

        {/* --- desktop: index rail + detail --- */}
        <div className="mt-10 hidden min-w-0 gap-8 lg:grid lg:grid-cols-12" data-id="element-220" data-mp="r9sapes2zyhz">
          <div className="min-w-0 lg:col-span-4" data-id="element-221" data-mp="m9mk81xjsi1h">
            <ul className="flex flex-col gap-1.5" data-id="element-222" data-mp="mc8u487wv9mv">
              {solutions.map((s, i) => {
              const isActive = s.key === active;
              const t = accent[s.accent];
              return <li key={s.key} className="min-w-0" data-id="element-223" data-mp="19zgwoidyg4l7" data-mpi={i}>
                    <button type="button" onClick={() => setActive(s.key)} aria-pressed={isActive} className={`group flex w-full items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-colors ${isActive ? `${t.border} bg-white/[0.06]` : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]'}`} data-id="element-224" data-mp="19yyu740p8u4u">
                      <span className={`font-mono text-[12px] tracking-[0.1em] ${isActive ? t.text : 'text-mist-400'}`} data-id="element-225" data-mp="36lzr61c157bp">
                        0{i + 1}
                      </span>
                      <span className={`min-w-0 text-[13.5px] font-semibold ${isActive ? 'text-white' : 'text-mist-200'}`} data-id="element-226" data-mp="3bmxhx2b3v9ot">
                        {s.label}
                      </span>
                      <ArrowRightIcon className={`ml-auto h-4 w-4 shrink-0 transition-transform ${isActive ? `${t.text} translate-x-0` : '-translate-x-1 text-white/20'}`} aria-hidden="true" data-id="element-227" data-mp="3kn2ai6bn43ix" />
                    </button>
                  </li>;
            })}
            </ul>
          </div>

          <div className="min-w-0 lg:col-span-8" data-id="element-228" data-mp="c5z3pogp6o5s">
            <AnimatePresence mode="wait" data-id="element-229" data-mp="c7invq5h5jcu">
              <motion.article key={current.key} initial={{
              opacity: 0,
              y: 14
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.28
            }} className="relative h-full min-w-0 rounded-2xl border border-white/10 bg-navy-950/80 p-8" data-id="element-230" data-mp="h522ir633y5b">
                <span className={`absolute inset-x-0 top-0 h-[3px] rounded-t-2xl ${tone.bg}`} aria-hidden="true" data-id="element-231" data-mp="da7odf1wdz39" />

                <div className="grid min-w-0 gap-8 lg:grid-cols-5" data-id="element-232" data-mp="8beb9nqiwa3m">
                  <div className="min-w-0 lg:col-span-3" data-id="element-233" data-mp="3gpddh7438rpk">
                    <p className={`font-mono text-[11.5px] uppercase tracking-[0.14em] ${tone.text}`} data-id="element-234" data-mp="3bqa7nkgaahgj">{current.lead}</p>
                    <h3 className="mt-3 font-display text-[1.5rem] font-extrabold leading-tight text-white" data-id="element-235" data-mp="3qo2qoml1hnzh">
                      {current.headline}
                    </h3>
                    <p className="mt-4 text-[14.5px] leading-relaxed text-mist-200" data-id="element-236" data-mp="15waknm7g630h">{current.body}</p>
                  </div>

                  <div className="min-w-0 lg:col-span-2" data-id="element-237" data-mp="10wxno8mv3fk6">
                    <p className="font-mono text-[11.5px] uppercase tracking-[0.14em] text-mist-400" data-id="element-238" data-mp="18hi0u1otcm0c">What that includes</p>
                    <ul className="mt-4 space-y-3" data-id="element-239" data-mp="1dgl6nocmaw9d">
                      {current.deliverables.map(d => <li key={d} className="flex min-w-0 gap-2.5 text-[13.5px] leading-snug text-mist-100" data-id="element-240" data-mp="zhlg2b5jo7ak">
                          <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${tone.soft}`} data-id="element-241" data-mp="13inkd6ebj9je">
                            <CheckIcon className={`h-3 w-3 ${tone.text}`} aria-hidden="true" data-id="element-242" data-mp="1r9sy9g0p7rr2" />
                          </span>
                          {d}
                        </li>)}
                    </ul>
                  </div>
                </div>
              </motion.article>
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-8 flex" data-id="element-243" data-mp="2a5sig4nfpr03">
          <Link to="/#get-started" className="group inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 py-3.5 text-[14.5px] font-semibold text-white transition-colors hover:border-forge-orange/60 hover:bg-white/5" data-id="element-244" data-mp="2a4hi6et771dq">
            Discuss What Needs to Improve
            <ArrowRightIcon className="h-4 w-4 text-forge-orange transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-245" data-mp="2fsmbgez2sxcs" />
          </Link>
        </div>
      </div>
    </section>;
}
type AccordionProps = {
  solution: Solution;
  open: boolean;
  onToggle: () => void;
};
function AccordionItem({
  solution,
  open,
  onToggle
}: AccordionProps) {
  const tone = accent[solution.accent];
  const panelId = `solution-panel-${solution.key}`;
  return <div className={`min-w-0 rounded-xl border ${open ? tone.border : 'border-white/10'} bg-white/[0.03]`} data-id="element-246" data-mp="1x2wjdzmp6pok">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={panelId} className="flex w-full items-center gap-3 px-4 py-3.5 text-left" data-id="element-247" data-mp="1s4l9jowekf6c">
        <span className={`min-w-0 flex-1 text-[14px] font-semibold ${open ? 'text-white' : 'text-mist-200'}`} data-id="element-248" data-mp="1i5m8oshvpntw">
          {solution.label}
        </span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform ${open ? `rotate-180 ${tone.text}` : 'text-white/35'}`} aria-hidden="true" data-id="element-249" data-mp="1czh2a8qs8f7s" />
      </button>

      {open && <div id={panelId} className="min-w-0 border-t border-white/10 px-4 py-4" data-id="element-250" data-mp="1mx4zr6frw0tn">
          <h3 className="font-display text-[16px] font-bold leading-snug text-white" data-id="element-251" data-mp="paxudubna4g8">{solution.headline}</h3>
          <p className="mt-2.5 text-[13.5px] leading-relaxed text-mist-200" data-id="element-252" data-mp="u8ppsybi08p6">{solution.body}</p>
          <ul className="mt-4 space-y-2.5" data-id="element-253" data-mp="z8bzwyw158tr">
            {solution.deliverables.map(d => <li key={d} className="flex min-w-0 gap-2.5 text-[13px] leading-snug text-mist-100" data-id="element-254" data-mp="13ztyxhehszc3">
                <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${tone.soft}`} data-id="element-255" data-mp="6kg3zweabky1">
                  <CheckIcon className={`h-3 w-3 ${tone.text}`} aria-hidden="true" data-id="element-256" data-mp="bhzkl2ygg9t5" />
                </span>
                {d}
              </li>)}
          </ul>
        </div>}
    </div>;
}